import {
  OnInit,
  OnDestroy,
  Directive,
  AfterViewInit,
  Injectable,
  Inject,
  Input,
  Optional,
  OnChanges,
  SimpleChanges,
  inject,
} from "@angular/core";
import {
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from "@angular/forms";
import { Router } from "@angular/router";
import {
  IResultPageState,
  IFormOptions,
  IEmbeddableForm,
  TNavigation,
  BaseFpxLoadFormHandler,
  BaseFpxChangeHandler,
  FindByKeyFn,
  BaseFpxControlEventHandler,
  BaseFpxDataService,
  BaseFpxPostSubmitInterceptor,
  FpxHelperAction,
} from "../models/index";
import {
  distinctUntilChanged,
  Observable,
  skip,
  Subject,
  Subscription,
  takeUntil,
} from "rxjs";
import { RoutingInfo } from "../models/routing-info.model";
import { SpinnerService } from "../services/spinner/spinner.service";

@Directive()
export abstract class BaseFpxFormComponent<
  Helper extends BaseFpxFormHelper<State>,
  State extends BaseFpxComponentState
> implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, Validator, OnChanges
{
  /** @description Template variables */
  formGroup: FormGroup;
  dataAvailable$: Subject<any> = new Subject<any>();
  _dataService!: BaseFpxDataService<unknown>;
  state: State;

  formChangePublisher$ = new Subject<{ subject: string; payload: any }>();

  /** @description Private Variables */
  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly _formBuilder: FormBuilder;
  private readonly _route: Router;
  public _helper: Helper;
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly formControlsStatus$: Subscription[] = [];
  private readonly formControlsValue$: Subscription[] = [];
  private unSubcribe$ = new Subject();
  private _serviceCode: string = "";
  private _findByIdfn: FindByKeyFn<any> | undefined;
  public routingKey: unknown;
  public isTabForm: boolean = false;

  private formGroupStatus$!: Subscription;

  private routingParams: any;
  @Input() formControlName!: string;
  formControl!: FormControl;
  private readonly _destroy$ = new Subject<any>();
  private readonly _controlContainer: ControlContainer;

  private primaryKeyList: string[] = [];

  presentInViewMode = (routingKey: { [T: string]: unknown }) => {
    const keys = Object.keys(routingKey);
    let primaryKeys: { [T: string]: unknown } = {};
    for (let index = 0; index < keys.length; index++) {
      if (keys[index] !== "action") {
        primaryKeys[keys[index]] = routingKey[keys[index]];
      }
    }
    this.formGroup.patchValue(primaryKeys);
    this.formGroup.disable();
    this._helper.spinner?.hide();
  };
  presentInEntryMode = () => {
    console.log("present in entry mode");
  };
  presentInRepairMode = (routingKey: { [T: string]: unknown }) => {
    const keys = Object.keys(routingKey);
    let primaryKeys: { [T: string]: unknown } = {};
    for (let index = 0; index < keys.length; index++) {
      if (keys[index] !== "serviceCode" && keys[index] !== "action") {
        primaryKeys[keys[index]] = routingKey[keys[index]];
      }
    }
    this.formGroup.patchValue(primaryKeys);
    this.formGroup.disable();
    this._helper.spinner?.hide();
  };
  presentInDecisionlMode = (routingKey: { [T: string]: unknown }) => {
    const keys = Object.keys(routingKey);
    let primaryKeys: { [T: string]: unknown } = {};
    // for (let index = 0; index < keys.length; index++) {
    //   if (keys[index] !== "serviceCode" && keys[index] !== "action") {
    //     primaryKeys[keys[index]] = routingKey[keys[index]];
    //   }
    // }
    const domainKey = (routingKey["domainKey"] as string)?.split("|");
    for (let index = 0; index < this.primaryKeyList.length; index++) {
      primaryKeys[this.primaryKeyList[index]] = domainKey[index];
    }
    this.formGroup.patchValue(primaryKeys);
    this.formGroup.disable();
    this._helper.spinner?.hide();
  };
  presentInModifyMode = (routingKey: { [T: string]: unknown }) => {
    const keys = Object.keys(routingKey);
    let primaryKeys: { [T: string]: unknown } = {};
    for (let index = 0; index < keys.length; index++) {
      if (
        // keys[index] !== 'serviceCode' &&
        keys[index] !== "action" &&
        keys[index] !== "serviceData"
      ) {
        primaryKeys[keys[index]] = routingKey[keys[index]];
      }
    }
    this.formGroup.disable();
    this.formGroup.patchValue(primaryKeys);
    this.formGroup.enable();
    this._helper.spinner?.hide();
    //this.formGroup.updateValueAndValidity();
    /*if (this._dataService.findByKey) {
      this._dataService
        ?.findByKey(this.formGroup.value)()
        .subscribe({
          next: (res: any) => {
            this.formGroup.patchValue(res);
          },
          error: () => {},
          complete: () => {},
        });
    }*/
  };

  private _modifyForm(payload: any) {
    const formValue = this.formGroup.value;
    const key = Object.keys(formValue);
    const transformationMap = this._helper.getTransformationMap();
    let data: { [key: string]: any } = {};
    for (let index = 0; index < key.length; index++) {
      if (transformationMap.has(key[index])) {
        let transformationKey = transformationMap.get(key[index]) ?? "";
        console.log(payload[transformationKey]);
      } else {
        data[key[index]] = payload[key[index]];
      }
    }
    this.formGroup.patchValue(data);
    this._helper.spinner?.hide();
  }

  private _embadableForm: IEmbeddableForm = {
    ADD: this.presentInEntryMode,
    DECISION: this.presentInDecisionlMode,
    REPAIR: this.presentInRepairMode,
    VIEW: this.presentInViewMode,
    MODIFY: this.presentInModifyMode,
  };

  public embadedFormMode: TNavigation = "ADD";

  constructor(
    _formBuilder: FormBuilder,
    _route: Router,
    _controlContainer: ControlContainer,
    _helper: Helper
  ) {
    this._formBuilder = _formBuilder;
    this._route = _route;
    this._helper = _helper;
    this._controlContainer = _controlContainer;
    this.formGroup = this._formBuilder.group({}, { updateOn: "blur" });
    this.state = _helper.getState;
    this._setRoutingParams();
  }

  setHelper(helper: Helper) {
    this._helper = helper;
  }
  writeValue(obj: any): void {
    if (obj) {
      this.formGroup.patchValue(obj, { emitEvent: false });
    }
  }
  onChange: any = () => {};
  onTouched: () => void = () => {};
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  validate(): ValidationErrors | null {
    return this.formGroup.invalid ? { invalid: true } : null;
  }
  onValidationChange: any = () => {};
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  ngAfterViewInit(): void {
    console.log("Inside ng after view init");
    this.doPostInit();
    if (this.embadedFormMode) {
      this._embadableForm[this.embadedFormMode](this.routingKey);
    }
    this._helper.doPostInit();
  }

  /** @description init lifecycle hook */
  ngOnInit(): void {
    this.doPreInit();
    this.formGroup.addValidators(this._syncValidators);
    this.formGroup.addAsyncValidators(this._asyncValidators);
    // this.formGroup.updateValueAndValidity();

    this.formGroupStatus$ = this.formGroup.statusChanges.subscribe(
      (status: FormControlStatus) => {
        //console.log("Form Status Change", status);
      }
    );
    console.log(this.embadedFormMode);
    this._registerFormControl();
    this._helperDependency();
    this._helper.setFormMode(this.embadedFormMode);
    this._helper.setFormGroup(this.formGroup);
    this._helper.doPreInit();
    this.dataAvailable$.subscribe((payload) => {
      if (
        this.embadedFormMode === "MODIFY" ||
        this.embadedFormMode === "DECISION" ||
        this.embadedFormMode === "VIEW"
      ) {
        if (payload) {
          console.log("Data available", payload);

          for (let li = 0; li < this.primaryKeyList.length; li++)
            delete payload[this.primaryKeyList[li]];

          this.clearNonPKFields();
          payload = this._helper.postDataFetchInterceptor(payload);
          this.formGroup.patchValue(payload);
          this._helper.spinner?.hide();
        } else {
          console.log("Data not available", payload);
          this.clearNonPKFields();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dongChange(changes);
  }

  private _helperDependency(){
    this._helper.helperAction$.asObservable().pipe(takeUntil(this._destroy$)).subscribe({
      next : ({ action , data }) => {
      this._helperAction[action]?.(data)
      },
      error : (err : any) => {
     
      },
      complete : () => {
     
      }
      });
  }
  private _setService = (data : any) => {
    if(data){
    this._dataService = data;
    }
    }
   
    private _helperAction : { [K in FpxHelperAction] : any } = {
    'SETDATASERVICE' : this._setService
    }
  private _registerFormControl() {
    if (this.formControlName) {
      let formGroup = this._controlContainer.control as FormGroup;
      this.formControl = formGroup.controls[
        this.formControlName
      ] as FormControl;
      this.formGroup.valueChanges.pipe(takeUntil(this._destroy$)).subscribe({
        next: (res) => {
          this.formControl.setValue(this.formGroup.value, {
            emitModelToViewChange: false,
          });
        },
      });
    }
  }

  /** @description destroy lifecycle hook */
  ngOnDestroy(): void {
    if (this.formGroupStatus$) {
      this.formGroupStatus$.unsubscribe();
    }
    while (this.formControlsStatus$.length > 0) {
      let subscription$ = this.formControlsStatus$.pop();
      if (subscription$) {
        subscription$.unsubscribe();
      }
    }
    while (this.formControlsValue$.length > 0) {
      let subscription$ = this.formControlsValue$.pop();
      if (subscription$) {
        subscription$.unsubscribe();
      }
    }
    this.doDestroy();
    this._helper.doDestroy();
  }

  getErrors(): String[] | null {
    return Object.keys(this.formGroup.errors as ValidationErrors);
  }

  removeNull(formValues:any){
    Object.keys(formValues).forEach(key => {
      if(typeof(formValues[key]) === 'object'){
        this.removeNull(formValues[key]);
      } else if (formValues[key] === null || formValues[key] === undefined || formValues[key] === "") {
        delete formValues[key];
      }
    });
    return formValues;
  }

  cancelPreSubmitForm(){
    return this._helper.cancelPreSubmitForm(
      this.removeNull(this.formGroup.getRawValue())
    );
  }
  preSubmit(){
    return this._helper.preSubmitForm(
      this.removeNull(this.formGroup.getRawValue())
    );
  }

  submit() {
    /**
     * convert formGroup.value to BusinessHour
     *  if form valid then service.create(BusinessHour) */
    this._helper.spinner?.show();
    var processedFormData: any;
    let routingInfo: RoutingInfo | null = new RoutingInfo();
    processedFormData = this._helper.preSubmitInterceptor(
      this.removeNull(this.formGroup.getRawValue())
    );
    console.log("inside Submit ", this.embadedFormMode, this.formGroup.valid);
    if (this.formGroup.valid && this._helper.getFormMode() === "ADD") {
      // this.doSubmit(this.formGroup);
      console.log("submitting the form in ADD mode");
      this._dataService
        .create(processedFormData)()
        .subscribe({
          next: (res: any) => {
            console.log(res);
            routingInfo = this._helper.postSubmitInterceptor({
              success: res,
              error: null,
            });
            this._helper.spinner?.hide();
            if (routingInfo && routingInfo['navigationURL'].length) {
              this._route.navigate(routingInfo.getNavigationURL()!, {
                queryParams: routingInfo.getQueryParams(),
                preserveFragment: true,
              });
            }
          },
          error: (err: any) => {
            routingInfo = this._helper.postSubmitInterceptor({
              success: null,
              error: err,
            });
            this._helper.spinner?.hide();
            if (routingInfo) {
              this._route.navigate(routingInfo.getNavigationURL()!, {
                queryParams: routingInfo.getQueryParams(),
                preserveFragment: true,
              });
            }
            console.error(err);
          },
          complete: () => {},
        });
    } else if (this.formGroup.valid && this._helper.getFormMode()  === "MODIFY") {
      // this.doModify(this.formGroup);
      this._dataService
        .modify(processedFormData)()
        .subscribe({
          next: (res: any) => {
            console.log(res);
            routingInfo = this._helper.postSubmitInterceptor({
              success: res,
              error: null,
            });
            this._helper.spinner?.hide();
            if (routingInfo && routingInfo['navigationURL'].length) {
              this._route.navigate(
                routingInfo.getNavigationURL()!,
                routingInfo.getQueryParams()
              );
            }
          },
          error: (err: any) => {
            routingInfo = this._helper.postSubmitInterceptor({
              success: null,
              error: err,
            });
            this._helper.spinner?.hide();
            if (routingInfo) {
              this._route.navigate(routingInfo.getNavigationURL()!, {
                queryParams: routingInfo.getQueryParams(),
                preserveFragment: true,
              });
            }
            console.error(err);
          },
          complete: () => {},
        });
    } else {
      this.formGroup.markAsTouched();
    }
  }

  handleControlEvent(event: { eventName: string; payload: any }) {
    this._helper.notifyControlEventHandler(event.eventName, event.payload);
  }

  reset() {
    this.formGroup.reset();
    this.doReset(this.formGroup);
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected addComponentState(name: string, defaultValue: any) {
    (this.state as any)[name] = defaultValue;
  }

  protected clearNonPKFields() {
    Object.keys(this.formGroup.controls).forEach((key) => {
      console.log("PK List", this.primaryKeyList);
      if (!this.primaryKeyList.includes(key)) {
        console.log("clearing control", key);
        this.formGroup.controls[key].reset();
      }
    });
  }

  protected addFormControl(
    name: string,
    defaultValue: any,
    syncValidatorFns: ValidatorFn[] = [],
    asyncValidatorFns: AsyncValidatorFn[] = [],
    updateOn: "change" | "blur" | "submit" = "blur",
    initialChangeDection: number = 1,
    isPrimaryKey: boolean = false,
    tabIndex: number = 0
  ) {
    let formControl = this._formBuilder.control(defaultValue, {
      validators: syncValidatorFns,
      asyncValidators: asyncValidatorFns,
      updateOn: updateOn,
      nonNullable: true
    });
    if (isPrimaryKey) {
      this.primaryKeyList.push(name);
    }
    this.formGroup.addControl(name, formControl);
    setTimeout(() => {
      this.formGroup.get(name)?.updateValueAndValidity();
    });
    formControl.statusChanges
      .pipe(takeUntil(this.unSubcribe$), skip(initialChangeDection))
      .subscribe((status: FormControlStatus) => {
        if (this.embadedFormMode === "MODIFY") {
          if (isPrimaryKey && status === "INVALID") this.clearNonPKFields();
        }

        this._helper.notifyChangeHandler(
          name,
          status,
          formControl.value,
          this.formGroup
        );
        this._helper.notifyStatusChangeHandler(
          name,
          status,
          formControl.value,
          this.formGroup
        );
      });
    formControl.valueChanges
      .pipe(takeUntil(this.unSubcribe$))
      .subscribe((value: any) => {
        this.formChangePublisher$.next({ subject: name, payload: value });
        this._helper.notifyValueChangeHandler(
          name,
          formControl.status,
          formControl.value,
          this.formGroup
        );
      });
    let control = this._helper.tabControl.get(tabIndex) || [];
    control.push(name);
    this._helper.tabControl.set(tabIndex, control);
  }

  protected addFormGroup(name: string, formGroup: FormGroup) {
    this.formGroup.addControl(name, formGroup);
    formGroup.statusChanges
      .pipe(takeUntil(this.unSubcribe$), distinctUntilChanged())
      .subscribe((status: FormControlStatus) => {
        console.log("Group Status Change", name, status);
        // this.doStatusChange(name, status, formGroup.value, formGroup.value);
        /** vetri changes for dynamic function call for each form control 30-06-2022 */
        this._helper.notifyChangeHandler(
          name,
          status,
          formGroup.value,
          this.formGroup
        );
        this._helper.notifyStatusChangeHandler(
          name,
          status,
          formGroup.value,
          this.formGroup
        );
      });
    formGroup.valueChanges
      .pipe(takeUntil(this.unSubcribe$))
      .subscribe((value: any) => {
        this.formChangePublisher$.next({ subject: name, payload: value });
        this._helper.notifyValueChangeHandler(
          name,
          formGroup.status,
          formGroup.value,
          this.formGroup
        );
      });
  }

  protected addFindById<T>(findByKeyFn: FindByKeyFn<T>) {
    this._findByIdfn = findByKeyFn;
  }

  protected setDataService(service: BaseFpxDataService<unknown>): void {
    this._dataService = service;
  }

  /**
   * @param name string or string []
   * @param isDisable boolean
   * @param opts IformOptions to emit valuechanges on disable
   */
  protected disableFormControl(
    name: string | string[],
    isDisable: boolean,
    opts: IFormOptions = { emitEvent: false, onlySelf: false }
  ): void {
    try {
      if (typeof name === "string") {
        isDisable
          ? this.formGroup.get(name)?.disable(opts)
          : this.formGroup.get(name)?.enable(opts);
      } else if (Array.isArray(name)) {
        for (let i = 0; i < name.length; i++) {
          const element = name[i];
          isDisable
            ? this.formGroup.get(element)?.disable(opts)
            : this.formGroup.get(element)?.enable(opts);
        }
      }
    } catch (e: any) {
      throw new Error(e);
    }
  }

  protected resetFormControl(
    name: string | string[],
    opts: IFormOptions | undefined = { emitEvent: false, onlySelf: false }
  ): void {
    try {
      if (typeof name === "string") {
        this.formGroup.get(name)?.reset(null, opts);
      } else if (Array.isArray(name)) {
        for (let i = 0; i < name.length; i++) {
          const element = name[i];
          this.formGroup.get(element)?.reset(null, opts);
        }
      }
    } catch (e: any) {
      throw new Error(e);
    }
  }

  protected setFormData(formData: any) {
    this.formGroup.setValue(formData);
  }

  protected disableForm() {
    this.formGroup.disable();
  }

  protected getRoutingParam(key: string) {
    return this.routingParams[key] || "";
  }

  protected doReset(formGroup: FormGroup): void {}
  protected doSubmit(formGroup: FormGroup): void {}
  protected doModify(formGroup: FormGroup): void {}
  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected dongChange(changes: SimpleChanges): void {}
  protected doDestroy(): void {}
  protected doStatusChange(
    subject: string,
    status: FormControlStatus,
    value: any,
    context: any
  ): void {}
  protected doChangeNotify(
    subject: string,
    payload: any,
    context: Map<string, any>
  ): void {}

  protected navigateToResultPage(resultContext: IResultPageState): void {
    resultContext.serviceCode = this._serviceCode;
    this._route.navigate(["confirmation"], {
      queryParams: resultContext,
      queryParamsHandling: "preserve",
    });
  }

  private _presentData<T>(navigateState: T): void {}

  private _setRoutingParams(): void {
    this.routingParams =
      this._route.getCurrentNavigation()?.extras?.queryParams;
    this._serviceCode =
      this._route.getCurrentNavigation()?.extras?.queryParams?.[
        "serviceCode"
      ] ?? "";
    this.embadedFormMode =
      this._route.getCurrentNavigation()?.extras?.queryParams?.["action"] ??
      "ADD";
    this.routingKey =
      this._route.getCurrentNavigation()?.extras.queryParams ?? {};
    this._helper.setRoutingParam(
      this._route.getCurrentNavigation()?.extras?.queryParams
    );
  }

  protected formValid(formcontrols: string[]): boolean {
    let formValid = formcontrols.map(
      (control) => this.formGroup.get(control)?.valid
    );
    return !formValid.every((controls) => controls);
  }

  protected addTab(
    name: string,
    icon: string | undefined = undefined,
    hidden: boolean = false
  ): void {
    this._helper.tabDetails.push({
      name: name,
      tabIndex: this._helper.tabDetails.length,
      icon: icon,
      hidden: hidden,
    });
    this.isTabForm = true;
  }
}

export abstract class BaseFpxComponentState {}

@Injectable()
export abstract class BaseFpxFormHelper<State extends BaseFpxComponentState> {
  protected state: State;
  private _formGroup!: FormGroup;
  private routingParams: any;
  private _transformationMap: Map<string, string> = new Map<string, string>();
  private formMode!: string;
  public spinner!: SpinnerService;
  protected handlerMap: Map<String, BaseFpxChangeHandler> = new Map();
  protected handlerValueChangeMap: Map<String, BaseFpxChangeHandler> =
    new Map();
  protected handlerStatusChangeMap: Map<String, BaseFpxChangeHandler> =
    new Map();
  protected tabIndex: number[] = [];
  public activeTab: FormControl = new FormControl(0);
  public tabDetails: Array<{
    name: string;
    tabIndex: number;
    icon: string | undefined;
    hidden: boolean;
  }> = [];

  public tabControl: Map<number, Array<string>> = new Map<
    number,
    Array<string>
  >();

  protected loadFormMap: Map<String, BaseFpxLoadFormHandler<any>> = new Map();

  protected controlEventMap: Map<string, BaseFpxControlEventHandler> =
    new Map();

  public helperAction$ : Subject<any> = new Subject<any>();
  constructor(@Inject("_state") _state: State) {
    this.state = _state;
  }

  public setFormMode(formMode: string) {
  
    this.formMode = formMode;
  }

  public getFormMode(): string {
    return this.formMode;
  }

  public get getState(): State {
    return this.state;
  }

  public setSpinnerService(spinnerService: SpinnerService) {
    this.spinner = spinnerService;
  }

  protected addChangeHandler(
    name: String,
    changeHandler: BaseFpxChangeHandler
  ) {
    this.handlerMap.set(name, changeHandler);
  }

  protected addValueChangeHandler(
    name: String,
    changeHandler: BaseFpxChangeHandler
  ) {
    this.handlerValueChangeMap.set(name, changeHandler);
  }

  protected addStatusChangeHandler(
    name: String,
    changeHandler: BaseFpxChangeHandler
  ) {
    this.handlerStatusChangeMap.set(name, changeHandler);
  }

  protected setTranformationMap(map: Map<string, string>) {
    this._transformationMap = map;
  }

  public getTransformationMap(): Map<string, string> {
    return this._transformationMap;
  }

  protected addLoadFormHandler(
    name: string,
    loadFormHandler: BaseFpxLoadFormHandler<any>
  ) {
    this.loadFormMap.set(name, loadFormHandler);
  }

  protected addControlEventHandler(
    eventName: string,
    eventFn: BaseFpxControlEventHandler
  ) {
    this.controlEventMap.set(eventName, eventFn);
  }

  /**
   * public notifyLoadFormHandler(
    name: string,
    taskId: string,
    formGroup: FormGroup
  ) {
    if (this.loadFormMap.has(name)) {
      const requestPayload = JSON.parse(
        this._shellService.workflowRowData?.requestPayload ?? '{}'
      );
      (this.loadFormMap as Map<string, any>).get(name)(
        taskId,
        formGroup,
        requestPayload
      );
    }
  }
   */

  public notifyChangeHandler(
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormGroup
  ) {
    if (this.handlerMap.has(name)) {
      (this.handlerMap as Map<string, any>).get(name)(
        name,
        status,
        value,
        formGroup
      );
    }
  }

  /** vetri changes for status change handler  */
  public notifyValueChangeHandler(
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormGroup
  ) {
    if (this.handlerValueChangeMap.has(name)) {
      (this.handlerValueChangeMap as Map<string, any>).get(name)(
        name,
        status,
        value,
        formGroup
      );
    }
  }
  /** vetri changes for value change handler  */
  public notifyStatusChangeHandler(
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormGroup
  ) {
    if (this.handlerStatusChangeMap.has(name)) {
      (this.handlerStatusChangeMap as Map<string, any>).get(name)(
        name,
        status,
        value,
        formGroup
      );
    }
  }

  public notifyControlEventHandler(eventName: string, payload: any) {
    if (this.controlEventMap.has(eventName)) {
      (this.controlEventMap as Map<string, any>).get(eventName)(payload);
    }
  }

  public setFormGroup(formGroup: FormGroup) {
    this._formGroup = formGroup;
  }
  public setRoutingParam(routingParam: any) {
    this.routingParams = routingParam;
  }

  protected getRoutingParam(key: string) {
    return this.routingParams?.[key] || "";
  }

  get formGroup(): FormGroup {
    return this._formGroup;
  }

  /** Newly added method */

  protected getValue(formcontrolName: string[]): string[] {
    return formcontrolName.map((control) => this.formGroup.get(control)?.value);
  }

  protected setValue(formcontrolName: string, value: any) {
    this.formGroup.get(formcontrolName)?.patchValue(value);
  }

  protected setDisable(formcontrolName: string[], isDisable: boolean) {
    formcontrolName.forEach((control) =>
      isDisable
        ? this.formGroup.get(control)?.disable()
        : this.formGroup.get(control)?.enable()
    );
  }

  public doPreInit(): void {}
  public doPostInit(): void {}
  public doDestroy(): void {}
  
  public preSubmitInterceptor(payload: any): any {
    return payload;
  }

  public preSubmitForm(payload: any): any {
    return payload;
  }

  public cancelPreSubmitForm(payload: any): any {
    return payload;
  }

  public postSubmitInterceptor(
    response: BaseFpxPostSubmitInterceptor
  ): RoutingInfo | null {
    let routingInfo: RoutingInfo = new RoutingInfo();
    return routingInfo;
  }

  postDataFetchInterceptor(payload: any): any {
    return payload;
  }

  public getActiveTab(): number {
    return this.activeTab?.value;
  }

  public tabControls(tabIndex: number): string[] {
    return this.tabControl.get(tabIndex) || [];
  }

  getTabValidity(tabIndex: number): boolean {
    let control = this.tabControl.get(tabIndex) || [];
    console.log("control", control);
    return control.some(
      (control: string) => !this.formGroup.get(control)?.valid
    );
  }
  public setTabHidden(tabIndex: number, state: boolean): void {
    this.tabDetails[tabIndex].hidden = state;
  }
  public setTabDisable(tabIndex: number, state: boolean): void {}

  public resetTabFields(tabIndex: number): void {
    let controls = this.tabControl.get(tabIndex) || [];
    for (let index = 0; index < controls.length; index++) {
      this.formGroup
        .get(controls[index])
        ?.reset(null, { emitEvent: false, onlySelf: true });
    }
  }
  /** set the data service dynamically based on the logic */
 public setDataService = (service : BaseFpxDataService<any>) => {
  this.helperAction$.next({ action : 'SETDATASERVICE' , data : service })
  }
}
